import React, { useMemo } from 'react';

import classnames from 'classnames';

import { TextButton } from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import { COMPONENTS, SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import CommonTrans from 'components/Globals/CommonTrans';
import { useDrawerContext } from 'components/Paywall/PaywallDrawerWrapper';
import PriceRange from 'components/Paywall/PriceRange';

import { TP } from 'constants/index';
import { SUBSCRIPTION_GROUP_DETAILS, SUBSCRIPTION_GROUP_IDENTIFIERS } from 'constants/subscriptions';

import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import useNavigate from 'utils/hooks/useNavigate';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import classes from './PlanGroupDetails.module.scss';

export const GroupFeature = ({ i18nKey, isGroupListingView = false }) => {
  const { scrollToFooter } = useDrawerContext();

  if (!i18nKey) {
    return null;
  }

  const fontSize = isGroupListingView ? 12 : 13;

  return (
    <CommonTrans
      key={i18nKey}
      ns="NS_APP_GLOBALS"
      i18nKey={i18nKey}
      components={{
        section: <section className={classes.features} />,
        title: <Typography variant="p" size={fontSize} weight="bold" />,
        p: <Typography variant="p" size={fontSize} className={classes.featuresList__item} />,
        ul: <ul className={classes.featuresList} />,
        li: <Typography variant="li" size={fontSize} className={classes.featuresList__item} />,
        scroll_testimonials: <TextButton onClick={scrollToFooter} />,
      }}
    />
  );
};

export const FeatureTable = ({ priceOptions = [], data }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');

  const priceHeaders = useMemo(() => priceOptions.map(option => option.price), [priceOptions]);

  return (
    <table className={classes.featureTable}>
      <thead>
        <tr>
          {priceHeaders.map(header => (
            <th key={header} className={classes.featureTable__column_price}>
              <Typography size={12} weight="bold" color="orange">
                {header}
              </Typography>
            </th>
          ))}
          <th className={classes.featureTable__column_feature}>
            <Typography size={12} weight="bold" color="purple">
              {t(`${TP}.FN_FEATURES`)}
            </Typography>
          </th>
        </tr>
      </thead>
      <tbody>
        {data.map(row => (
          <tr key={row.feature}>
            {priceHeaders.map(price => (
              <td key={price} className={classes.featureTable__column_price}>
                {row.prices.includes(price) ? (
                  <SpriteIcon icon="check" size={12} className={classes.featureTable__column_price_check} />
                ) : (
                  <SpriteIcon icon="lock_black_24dp" size={12} />
                )}
              </td>
            ))}
            <td className={classes.featureTable__column_feature}>
              <Typography size={12}>{t(row.feature)}</Typography>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const PlanGroupDetails = ({
  identifier,
  skipPrice = false,
  showBasicInfo = false,
  isGroupListingView = false,
  styles,
  trackingData = {},
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const navigate = useNavigate();
  const { isDesktop } = useDeviceTypeLayouts();
  const { title, description, price, priceOptions, priceInclusions, features, tools, featureTable } =
    SUBSCRIPTION_GROUP_DETAILS[identifier] || {};

  const artistLinkProps = useMemo(() => {
    if (identifier !== SUBSCRIPTION_GROUP_IDENTIFIERS.ARTIST_PROFESSIONAL) {
      return null;
    }

    return navigate.getLinkProps({ entityType: ENTITY_TYPES.PROFILE });
  }, [navigate, identifier]);

  const fontSizes = useMemo(() => {
    if (isGroupListingView) {
      return {
        title: {
          size: showBasicInfo ? 13 : 16,
          weight: showBasicInfo ? 'medium' : 'bold',
        },
        description: {
          size: showBasicInfo ? 11 : 12,
          weight: 'normal',
        },
      };
    }

    return {
      title: {
        size: 16,
        weight: 'bold',
      },
      description: {
        size: isDesktop ? 14 : 13,
        weight: 'medium',
      },
    };
  }, [isGroupListingView, showBasicInfo, isDesktop]);

  return (
    <div className={classnames({ [styles?.root]: !!styles?.root })}>
      <div className={classes.primaryInfo}>
        <Typography variant="p" weight={fontSizes.title.weight} size={fontSizes.title.size}>
          {t(title)}
        </Typography>
        <div className={classnames(classes.description, { [styles?.description]: !!styles?.description })}>
          <Typography variant="p" size={fontSizes.description.size} weight="medium">
            {t(description)}{' '}
            {artistLinkProps && (!isGroupListingView || isDesktop) && (
              <Typography variant={isDesktop ? 'span' : 'p'}>
                <TextButton
                  {...artistLinkProps}
                  rightIcon={<SpriteIcon icon="chevron_right" size={12} />}
                  target="_blank"
                  isLink
                  trackingData={{
                    ...trackingData,
                    component: COMPONENTS.PROFESSIONS,
                    subComponent: SUB_COMPONENTS.VIEW_ALL_CTA,
                  }}
                >
                  <Typography size={fontSizes.description.size} color="inherit">
                    {t(`${TP}.VIEW_PROFESSIONS`)}
                  </Typography>
                </TextButton>
              </Typography>
            )}
          </Typography>
          {priceInclusions && isGroupListingView && !showBasicInfo && (
            <Typography variant="p" size={10} weight="medium" className={classes.priceInclusions}>
              {t(priceInclusions)}
            </Typography>
          )}
        </div>
      </div>
      {!showBasicInfo && (
        <div className={classes.featuresContainer}>
          {!skipPrice && (
            <>
              {price && !priceOptions && <PriceRange price={price} />}
              {priceOptions && (
                <div className={classes.priceOptionsContainer}>
                  {priceOptions.map((option, index) => (
                    <div key={index} className={classes.priceOption}>
                      <PriceRange price={option.price} isMonthly={option.isMonthly} priceSize={14} />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <GroupFeature i18nKey={features} isGroupListingView={isGroupListingView} trackingData={trackingData} />
          {!isGroupListingView && <GroupFeature i18nKey={tools} isGroupListingView={isGroupListingView} />}
          {priceOptions && featureTable && !isGroupListingView && (
            <FeatureTable priceOptions={priceOptions} data={featureTable} />
          )}
        </div>
      )}
    </div>
  );
};

export default PlanGroupDetails;
